// Customer Tickets / Roadmap tabs — live Jira issues for a customer, backed by
// GET /tickets?customer=&kind=tickets|roadmap. Shared table + metrics; kind
// selects the issuetype slice. VisitDetail's "Open tickets" panel reuses
// TicketsTable + statusTone / NoTicketMappingNote so the surfaces stay consistent.
import { useCallback, useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import {
  MetricCard, DataTable, Badge, FilterGroup, Alert, EmptyState, CenteredSpinner, Grid,
  type Column, type FilterRow,
} from "../../design/ui";
import {
  getCustomerTickets,
  type CustomerTickets,
  type OrgIssueKind,
  type Ticket,
  type TicketStatusCategory,
} from "../../lib/brain-api";

const ALL = "all";

/** Ticket workflow status isn't an entity lifecycle, so StatusBadge's
 *  active/onboarding/paused enum doesn't fit — a status-category-toned Badge
 *  (info=new, warning=in progress, success=done) reads correctly instead. */
export function statusTone(cat: TicketStatusCategory): "info" | "warning" | "success" {
  if (cat === "done") return "success";
  if (cat === "indeterminate") return "warning";
  return "info";
}

function formatUpdated(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
}

const ticketColumns: Column<Ticket>[] = [
  {
    key: "key",
    header: "Key",
    width: 110,
    render: (t) => (
      <a
        href={t.url}
        target="_blank"
        rel="noreferrer"
        style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--kt-teal-ink)", textDecoration: "none" }}
      >
        {t.key}
      </a>
    ),
  },
  {
    key: "summary",
    header: "Summary",
    render: (t) => <span style={{ color: "var(--kt-ink)" }}>{t.summary}</span>,
  },
  {
    key: "status",
    header: "Status",
    render: (t) => <Badge tone={statusTone(t.statusCategory)}>{t.status}</Badge>,
  },
  {
    key: "assignee",
    header: "Assignee",
    render: (t) => t.assignee ?? <span style={{ color: "var(--kt-text-muted)" }}>Unassigned</span>,
  },
  {
    key: "priority",
    header: "Priority",
    render: (t) => t.priority ?? "—",
  },
  {
    key: "updated",
    header: "Updated",
    sortValue: (t) => t.updated,
    render: (t) => formatUpdated(t.updated),
  },
];

/** Shared ticket table — used by both the full Tickets tab and VisitDetail's
 *  compact "Open tickets" panel. */
export function TicketsTable({ tickets }: { tickets: Ticket[] }) {
  return <DataTable columns={ticketColumns} rows={tickets} getRowId={(t) => t.key} empty="No tickets." />;
}

/** Explains the no-mapping state (no JSM org auto-matched server-side) — not
 *  an error, just nothing to show yet. Reused by VisitDetail's compact panel. */
export function NoTicketMappingNote() {
  return (
    <Alert tone="info" icon="building-off">
      No Jira Service Management organization matches this customer&apos;s aliases, so ticket
      data isn&apos;t available yet. Create the organization in JSM with the customer&apos;s
      alias as its name (or add the org&apos;s exact name as an alias on the record).
    </Alert>
  );
}

const COPY: Record<OrgIssueKind, { loading: string; emptyTitle: string; emptyBody: string; openLabel: string }> = {
  tickets: {
    loading: "Loading tickets…",
    emptyTitle: "No tickets",
    emptyBody: "Nothing found for this customer's Jira organization yet (non-Roadmap issues).",
    openLabel: "Open",
  },
  roadmap: {
    loading: "Loading roadmap…",
    emptyTitle: "No roadmap items",
    emptyBody: "No Roadmap-type issues found for this customer's Jira organization yet.",
    openLabel: "Open",
  },
};

export function CustomerTicketsTab({
  customerId,
  kind = "tickets",
}: {
  customerId: string;
  kind?: OrgIssueKind;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [data, setData] = useState<CustomerTickets | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>(ALL);
  const copy = COPY[kind];

  const load = useCallback(async () => {
    try {
      const result = await getCustomerTickets(customerId, kind);
      setData(result);
      setError(null);
    } catch (e) {
      const msg = e instanceof Error ? e.message : `Failed to load ${kind}`;
      setError(msg);
      enqueueSnackbar(msg, { variant: "error" });
    }
  }, [customerId, kind, enqueueSnackbar]);

  useEffect(() => {
    setData(null);
    setFilter(ALL);
    void load();
  }, [load]);

  if (error) return <Alert tone="danger">{error}</Alert>;
  if (!data) return <CenteredSpinner label={copy.loading} />;
  if (data.orgId === null) return <NoTicketMappingNote />;

  const { counts, tickets } = data;
  const newCount = tickets.filter((t) => t.statusCategory === "new").length;
  const inProgressCount = tickets.filter((t) => t.statusCategory === "indeterminate").length;
  const doneCount = tickets.filter((t) => t.statusCategory === "done").length;

  const filterRows: FilterRow[] = [
    { key: ALL, label: "All", count: tickets.length },
    { key: "new", label: "New", count: newCount },
    { key: "indeterminate", label: "In progress", count: inProgressCount },
    { key: "done", label: "Done", count: doneCount },
  ];
  const filtered = filter === ALL ? tickets : tickets.filter((t) => t.statusCategory === filter);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <Grid cols="3">
        <MetricCard label={copy.openLabel} value={counts.open} />
        <MetricCard label="In progress" value={counts.inProgress} />
        <MetricCard label="Done (30d)" value={counts.done30d} />
      </Grid>

      {tickets.length === 0 ? (
        <EmptyState icon="ticket" title={copy.emptyTitle}>
          {copy.emptyBody}
        </EmptyState>
      ) : (
        <>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, margin: "0 -22px" }}>
            <FilterGroup label="Status" rows={filterRows} mode="single" selected={filter} onSelect={setFilter} />
          </div>
          <TicketsTable tickets={filtered} />
        </>
      )}
    </div>
  );
}
